import { useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';

export interface UserProfile {
  sub: string;
  email?: string;
  birthdate?: string;
  interests?: string[];
  [key: string]: any;
}

export function useUserProfileApi(apiBaseUrl: string) {
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/profile`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${auth.user?.id_token}`,
        },
      });
      
      if (res.status === 404) {
        // Profile doesn't exist, create it
        const createRes = await fetch(`${apiBaseUrl}/profile`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.user?.id_token}`,
          },
          body: JSON.stringify({ sub: auth.user?.profile.sub }),
        });
        
        if (createRes.status === 409) {
          // Profile was created between our GET and POST - fetch it again
          const retryRes = await fetch(`${apiBaseUrl}/profile`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${auth.user?.id_token}`,
            },
          });
          if (!retryRes.ok) {
            throw new Error(`Error fetching profile: ${retryRes.status}`);
          }
          const data = await retryRes.json();
          setProfile(data.profile || data);
        } else if (!createRes.ok) {
          throw new Error(`Error creating profile: ${createRes.status}`);
        } else {
          const data = await createRes.json();
          setProfile(data.profile || data);
        }
      } else if (!res.ok) {
        throw new Error(`Error: ${res.status}`);
      } else {
        const data = await res.json();
        setProfile(data.profile || data);
      }
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, auth.user]);

  const createProfile = useCallback(async (body: Partial<UserProfile>) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.user?.id_token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Error: ${res.status}`);
      const data = await res.json();
      setProfile(data.profile || data);
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, auth.user]);

  return { profile, loading, error, fetchProfile, createProfile };
} 